/*
 * Game sounds, synthesised with the Web Audio API: no audio files to download, nothing to license.
 * Browsers only allow audio after the player touches the page, so the context is created on the first
 * pointer/key press. The on/off choice is saved on this device. Sounds never wait on, or hold back,
 * the game state: they are fired after the server's answer has already been drawn.
 */
import { useSyncExternalStore } from "react";

const KEY = "samra-sound";
let enabled = (() => {
  try {
    return localStorage.getItem(KEY) !== "off";
  } catch {
    return true;
  }
})();
const subs = new Set<() => void>();
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let noiseBuf: AudioBuffer | null = null;

function ensure(): AudioContext | null {
  if (!enabled) return null;
  try {
    if (!ctx) {
      const AC = window.AudioContext || (window as any).webkitAudioContext;
      if (!AC) return null;
      ctx = new AC();
      master = ctx.createGain();
      master.gain.value = 0.55;
      const comp = ctx.createDynamicsCompressor();
      master.connect(comp).connect(ctx.destination);
      noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 0.5, ctx.sampleRate);
      const d = noiseBuf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    }
    if (ctx.state === "suspended") void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

// Unlock on the first touch / click / key, as browsers require.
if (typeof window !== "undefined") {
  const unlock = () => {
    ensure();
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("keydown", unlock);
}

export function soundOn() {
  return enabled;
}
export function setSound(on: boolean) {
  enabled = on;
  try {
    localStorage.setItem(KEY, on ? "on" : "off");
  } catch {
    /* private mode: the choice lasts for this visit */
  }
  if (on) ensure();
  subs.forEach((f) => f());
}
export function useSoundOn() {
  return useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    () => enabled,
  );
}

/* ── building blocks ── */
function tone(freq: number, at: number, dur: number, opts: { type?: OscillatorType; vol?: number; to?: number; attack?: number } = {}) {
  const c = ensure();
  if (!c || !master) return;
  const t0 = c.currentTime + at;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = opts.type ?? "sine";
  o.frequency.setValueAtTime(freq, t0);
  if (opts.to) o.frequency.exponentialRampToValueAtTime(opts.to, t0 + dur);
  const v = opts.vol ?? 0.3;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(v, t0 + (opts.attack ?? 0.005));
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g).connect(master);
  o.start(t0);
  o.stop(t0 + dur + 0.02);
}

function noise(at: number, dur: number, opts: { freq?: number; q?: number; vol?: number; type?: BiquadFilterType; sweepTo?: number } = {}) {
  const c = ensure();
  if (!c || !master || !noiseBuf) return;
  const t0 = c.currentTime + at;
  const src = c.createBufferSource();
  src.buffer = noiseBuf;
  const f = c.createBiquadFilter();
  f.type = opts.type ?? "bandpass";
  f.frequency.setValueAtTime(opts.freq ?? 2000, t0);
  if (opts.sweepTo) f.frequency.exponentialRampToValueAtTime(opts.sweepTo, t0 + dur);
  f.Q.value = opts.q ?? 1;
  const g = c.createGain();
  const v = opts.vol ?? 0.4;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(v, t0 + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(f).connect(g).connect(master);
  src.start(t0, Math.random() * 0.3, dur + 0.05);
}

/* ── the sounds ── */
export const sfx = {
  /** Dice shaken in the hand, then landing. */
  dice() {
    for (let i = 0; i < 6; i++) noise(i * 0.045 + Math.random() * 0.015, 0.035, { freq: 2500 + Math.random() * 1500, q: 4, vol: 0.35 });
    noise(0.3, 0.07, { freq: 900, q: 2, vol: 0.55 });
    tone(140, 0.3, 0.09, { type: "triangle", vol: 0.35, to: 90 });
    noise(0.4, 0.04, { freq: 1600, q: 3, vol: 0.25 });
  },
  /** A card flicked onto the pile. */
  card() {
    noise(0, 0.12, { type: "highpass", freq: 1200, sweepTo: 5000, vol: 0.28 });
    noise(0.09, 0.05, { freq: 700, q: 1.5, vol: 0.3 });
  },
  /** Picking a card up from the hand. */
  cardPick() {
    noise(0, 0.05, { type: "highpass", freq: 3000, vol: 0.16 });
  },
  /** One hop of a marble or token: a short wooden tock. */
  step(i = 0) {
    tone(620 + (i % 2) * 40, 0, 0.06, { type: "triangle", vol: 0.22, to: 380 });
    noise(0, 0.025, { freq: 3000, q: 6, vol: 0.12 });
  },
  /** A piece set down (bringing a marble out, a domino on the table). */
  place() {
    tone(220, 0, 0.12, { type: "triangle", vol: 0.35, to: 140 });
    noise(0, 0.05, { freq: 1200, q: 2, vol: 0.35 });
  },
  /** Knocking another player's piece back to base. */
  capture() {
    noise(0, 0.08, { freq: 900, q: 1, vol: 0.5 });
    tone(520, 0.02, 0.14, { type: "square", vol: 0.12, to: 260 });
    tone(330, 0.12, 0.22, { type: "triangle", vol: 0.3, to: 120 });
  },
  /** Swapping two marbles. */
  swap() {
    tone(500, 0, 0.12, { type: "sine", vol: 0.2, to: 800 });
    tone(800, 0.1, 0.12, { type: "sine", vol: 0.2, to: 500 });
  },
  /** A piece reaches home. */
  home() {
    [660, 880, 1100].forEach((f, i) => tone(f, i * 0.07, 0.25, { type: "sine", vol: 0.2 }));
  },
  /** It's your turn. */
  turn() {
    tone(784, 0, 0.18, { type: "sine", vol: 0.18 });
    tone(1046, 0.11, 0.26, { type: "sine", vol: 0.18 });
  },
  /** Nothing playable / a refused move. */
  nope() {
    tone(220, 0, 0.12, { type: "square", vol: 0.08, to: 180 });
  },
  /** A cue or striker hitting. */
  hit() {
    noise(0, 0.04, { freq: 2200, q: 2, vol: 0.5 });
    tone(900, 0, 0.05, { type: "triangle", vol: 0.2, to: 500 });
  },
  /** Ball or coin dropping into a pocket. */
  pocket() {
    tone(180, 0, 0.18, { type: "sine", vol: 0.35, to: 70 });
    noise(0, 0.08, { freq: 500, q: 1, vol: 0.25 });
  },
  /** Memory: two cards match. */
  match() {
    tone(880, 0, 0.15, { vol: 0.2 });
    tone(1320, 0.08, 0.22, { vol: 0.2 });
  },
  /** End of a match. */
  win() {
    [523, 659, 784, 1046].forEach((f, i) => tone(f, i * 0.12, 0.35, { type: "triangle", vol: 0.22 }));
    tone(1318, 0.5, 0.6, { type: "sine", vol: 0.18 });
  },
};

/** Hop sounds spaced like the on-board animation, so ticks line up with the piece. */
export function steps(n: number, spacingMs: number) {
  const c = ensure();
  if (!c) return;
  for (let i = 0; i < Math.min(n, 14); i++) setTimeout(() => sfx.step(i), i * spacingMs);
}

/**
 * Sounds for a batch of server events. Piece hops are played by the boards (they know the animation);
 * everything else is played here, for every game.
 */
export function playEvents(gameId: string, events: any[]) {
  if (!enabled || !events?.length) return;
  const types = new Set(events.map((e) => e.type));
  if (gameId === "jackaroo") {
    if (types.has("discarded") || types.has("moved") || types.has("exited") || types.has("swapped")) sfx.card();
    if (types.has("exited")) setTimeout(sfx.place, 120);
    if (types.has("swapped")) setTimeout(sfx.swap, 120);
    if (types.has("dealt") || types.has("reshuffled")) setTimeout(() => [0, 1, 2].forEach((i) => setTimeout(sfx.card, i * 90)), 300);
  }
  if (types.has("rolled")) sfx.dice();
  if (types.has("no_moves") || types.has("three_sixes")) setTimeout(sfx.nope, 450);
  if (types.has("captured")) setTimeout(sfx.capture, gameId === "ludo" ? 380 : 300);
  if (types.has("token_home")) setTimeout(sfx.home, 400);
  if (types.has("played") || types.has("drew")) sfx.place();
  if (types.has("shot")) sfx.hit();
  const shot = events.find((e) => e.type === "shot");
  if (shot?.pocketed?.length) setTimeout(sfx.pocket, 600);
  if (types.has("flipped")) sfx.cardPick();
  if (types.has("matched")) setTimeout(sfx.match, 150);
  if (types.has("game_won") || types.has("player_finished") || types.has("match_end") || types.has("board_won") || types.has("eight_potted")) setTimeout(sfx.win, 500);
}
